import React from 'react';
import { Clock, CheckCircle, XCircle, User, Calendar, Mail, Phone, FileText, Volume2, Link as LinkIcon, Check, Video } from 'lucide-react';
import Button from '../common/Button';
import { updateJobApplicationStatus } from '../../lib/supabase/applications';
import type { JobApplication } from '../../types/applications';

interface ApplicationsListProps {
  applications: JobApplication[];
  onStatusUpdate?: () => void;
}

export default function ApplicationsList({
  applications,
  onStatusUpdate
}: ApplicationsListProps) {
  const [updatingId, setUpdatingId] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  const handleStatusUpdate = async (applicationId: string, status: JobApplication['status']) => {
    setUpdatingId(applicationId);
    
    try {
      const { error } = await updateJobApplicationStatus(applicationId, { status });
      if (error) throw error;
      
      if (onStatusUpdate) onStatusUpdate();
    } catch (err) {
      console.error('Error updating application status:', err);
      alert((err as Error).message);
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusIcon = (status: JobApplication['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'accepted':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'withdrawn':
        return <XCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: JobApplication['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'withdrawn':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (applications.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No applications yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {applications.map((application) => (
        <div key={application.id} className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              {getStatusIcon(application.status)}
              <div>
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-900">Application</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(application.status)}`}>
                    {application.status}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>Applied {new Date(application.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {application.status === 'pending' && (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStatusUpdate(application.id, 'accepted')}
                  isLoading={updatingId === application.id}
                  className="text-green-600 border-green-600 hover:bg-green-50"
                >
                  Accept
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStatusUpdate(application.id, 'rejected')}
                  isLoading={updatingId === application.id}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  Reject
                </Button>
              </div>
            )}
          </div>

          {/* Contact Information and Resume */}
          {(application.applicantEmail || application.applicantPhone || application.resumeUrl) && (
            <div className="bg-gray-50 rounded-md p-3 mb-3">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Contact Information</h4>
              <div className="space-y-1 text-sm">
                {application.applicantEmail && (
                  <div className="flex items-center text-gray-700">
                    <Mail className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{application.applicantEmail}</span>
                  </div>
                )}
                {application.applicantPhone && (
                  <div className="flex items-center text-gray-700">
                    <Phone className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{application.applicantPhone}</span>
                  </div>
                )}
                {application.resumeUrl && (
                  <div className="flex items-center mt-2">
                    <a
                      href={application.resumeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      View Resume
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {application.voiceIntroUrl && (
            <div className="bg-gray-50 rounded-md p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Volume2 className="h-4 w-4 text-green-600" />
                  <h4 className="text-sm font-medium text-gray-900">Voice Introduction</h4>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  icon={copiedId === application.id ? Check : LinkIcon}
                  onClick={() => {
                    navigator.clipboard.writeText(application.voiceIntroUrl!);
                    setCopiedId(application.id);
                    setTimeout(() => setCopiedId(null), 2000);
                  }}
                  className="text-xs"
                >
                  {copiedId === application.id ? 'Copied!' : 'Copy Link'}
                </Button>
              </div>
              <audio
                controls
                className="w-full"
                style={{ height: '40px' }}
              >
                <source src={application.voiceIntroUrl} type="audio/webm" />
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          {application.videoUrl && (
            <div className="bg-gray-50 rounded-md p-3 mb-3">
              <div className="flex items-center space-x-2 mb-2">
                <Video className="h-4 w-4 text-green-600" />
                <h4 className="text-sm font-medium text-gray-900">Video Introduction</h4>
              </div>
              <video
                controls
                className="w-full rounded-lg shadow-sm"
                preload="metadata"
              >
                <source src={application.videoUrl} type="video/mp4" />
                <source src={application.videoUrl} type="video/webm" />
                <source src={application.videoUrl} type="video/quicktime" />
                Your browser does not support the video tag.
              </video>
            </div>
          )}

          {application.message && (
            <div className="bg-gray-50 rounded-md p-3">
                <h4 className="text-sm font-medium text-gray-900 mb-1">Cover Letter:</h4>
              <p className="text-sm text-gray-700">{application.message}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}